import { Check, Mail, PencilIcon, X } from 'lucide-react';
import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

import { useInlineEdit } from '../hooks/useInlineEdit';

interface CMSEmailBadgeProps {
  matterId: string;
  cmsEmail?: string | null;
}

export const CMSEmailBadge: React.FC<CMSEmailBadgeProps> = ({
  matterId,
  cmsEmail,
}) => {
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const {
    isEditing,
    setIsEditing,
    editValue,
    setEditValue,
    handleSave,
    handleCancel,
    isLoading,
  } = useInlineEdit({
    matterId,
    initialValue: cmsEmail || '',
    fieldName: 'cms_email',
    onSuccessMessage: 'CMS Email updated successfully',
    transformValue: (val) => {
      const emailToSave = val.trim();
      return emailToSave || null;
    },
    validate: (val) => {
      const emailToSave = val.trim();
      if (!emailToSave) return true;
      return isValidEmail(emailToSave) ? true : 'Please enter a valid email';
    },
  });

  return (
    <div className="inline-flex items-center gap-0.5">
      {cmsEmail ? (
        <>
          <a
            href={`mailto:${cmsEmail}`}
            className="inline-flex items-center gap-1 text-xs sm:text-[11px] lg:text-xs hover:bg-zinc-100 px-1.5 py-0.5 rounded transition-colors bg-zinc-50"
            aria-label="Send email to CMS"
          >
            <Mail className="h-3 w-3" />
            <span className="font-medium">Email</span>
          </a>
          <Popover
            open={isEditing}
            onOpenChange={(open) => {
              if (!open) handleCancel();
              else setIsEditing(true);
            }}
          >
            <PopoverTrigger asChild>
              <button
                onClick={() => setIsEditing(true)}
                className="h-6 w-6 hover:bg-zinc-100 rounded flex items-center justify-center transition-colors"
                aria-label="Edit CMS Email"
                type="button"
              >
                <PencilIcon className="h-3 w-3 text-zinc-500" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="p-2 w-auto" align="start" side="bottom">
              <div className="flex items-center gap-1">
                <div className="relative">
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="matter-12345@firm.filevine.io"
                    className="pr-16 h-7 text-xs min-w-[180px] sm:min-w-[240px]"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSave();
                      if (e.key === 'Escape') handleCancel();
                    }}
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                    <button
                      onClick={handleSave}
                      disabled={isLoading}
                      className="h-5 w-5 hover:bg-green-100 rounded flex items-center justify-center"
                      type="button"
                    >
                      <Check className="h-3 w-3 text-green-600" />
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isLoading}
                      className="h-5 w-5 hover:bg-red-100 rounded flex items-center justify-center"
                      type="button"
                    >
                      <X className="h-3 w-3 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </>
      ) : (
        <Popover
          open={isEditing}
          onOpenChange={(open) => {
            if (!open) handleCancel();
            else setIsEditing(true);
          }}
        >
          <PopoverTrigger asChild>
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1 text-xs sm:text-[11px] lg:text-xs hover:bg-zinc-100 px-1.5 py-0.5 rounded transition-colors bg-zinc-50"
              aria-label="Add CMS Email"
              type="button"
            >
              <Mail className="h-3 w-3" />
              <span className="font-medium">Add CMS Email</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-2 w-auto" align="start" side="bottom">
            <div className="flex items-center gap-1">
              <div className="relative">
                <Input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="matter-12345@firm.filevine.io"
                  className="pr-16 h-7 text-xs min-w-[180px] sm:min-w-[240px]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') handleCancel();
                  }}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-0.5">
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="h-5 w-5 hover:bg-green-100 rounded flex items-center justify-center"
                    type="button"
                  >
                    <Check className="h-3 w-3 text-green-600" />
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="h-5 w-5 hover:bg-red-100 rounded flex items-center justify-center"
                    type="button"
                  >
                    <X className="h-3 w-3 text-red-600" />
                  </button>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
